import { getPlanMaster } from '../master/plan_master'
import type { PlanRes, RoomRes, TenantRes } from '../res'
import {
  isEchoesPlanMasterId,
  PLAN_MASTER_ID_PLUS,
} from '../type/plan_master_type'
import * as planLib from './plan_lib'
import * as tenantsLib from './tenants_lib'

export type Room = {
  tenants?: { startDate: string; endDate: string | null }[] | unknown[]
  plans?: { startDate: string; endDate: string | null }[] | unknown[]
  property?: unknown
  size?: number
  floor?: number
}

export type RoomLike = RoomRes | Room
type TenantReference = Pick<TenantRes, 'id'>

/**
 * 部屋の現在入居中のテナントを取得（日付ベース）
 */
export const roomActiveTenant = (
  room: RoomLike | undefined | null,
): TenantRes | null => {
  const tenants = (room as RoomRes | undefined | null)?.tenants as
    | TenantRes[]
    | undefined
  if (!tenants || tenants.length === 0) return null
  const currentTenants = tenantsLib.getCurrentTenants(
    tenants as any,
    null,
    null,
  )
  return (
    currentTenants.length > 0 ? currentTenants[0] : null
  ) as TenantRes | null
}

/**
 * 部屋の入居予定テナントを取得（日付ベース）
 */
export const roomPlannedTenant = (
  room: RoomLike | undefined | null,
): TenantRes | null => {
  const tenants = (room as RoomRes | undefined | null)?.tenants as
    | TenantRes[]
    | undefined
  if (!tenants || tenants.length === 0) return null
  const futureTenants = tenantsLib
    .getFutureTenants(tenants, null, null)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
  return (
    futureTenants.length > 0 ? futureTenants[0] : null
  ) as TenantRes | null
}

/**
 * 部屋の現在のテナントを取得（入居中優先、なければ入居予定）
 */
export const roomCurrentTenant = (
  room: RoomLike | undefined | null,
): TenantRes | null => {
  const activeTenant = roomActiveTenant(room)
  if (activeTenant) return activeTenant
  return roomPlannedTenant(room)
}

export const roomPlansForTenant = (
  room: RoomLike | undefined | null,
  tenant: TenantReference | undefined | null,
): PlanRes[] => {
  const plans = (room as RoomRes | undefined | null)?.plans as
    | PlanRes[]
    | undefined
  if (!plans || plans.length === 0) {
    return []
  }
  if (!tenant) {
    return []
  }
  return plans.filter((plan) => plan.tenant?.id === tenant.id)
}

export const roomActivePlan = (
  room: RoomLike | undefined | null,
  tenant?: TenantReference | null,
): PlanRes | null => {
  const plans = tenant
    ? roomPlansForTenant(room, tenant)
    : ((room as RoomRes | undefined | null)?.plans as PlanRes[] | undefined)
  if (!plans || plans.length === 0) {
    return null
  }
  const activePlan = plans.find((plan) => planLib.isCurrent(plan, null))
  return activePlan ?? null
}

export const roomPlannedPlan = (
  room: RoomLike | undefined | null,
  tenant?: TenantReference | null,
): PlanRes | null => {
  const plans = tenant
    ? roomPlansForTenant(room, tenant)
    : ((room as RoomRes | undefined | null)?.plans as PlanRes[] | undefined)
  if (!plans || plans.length === 0) {
    return null
  }
  const plannedPlan = plans.find((plan) => planLib.isFuture(plan, null))
  return plannedPlan ?? null
}

export const roomCurrentPlan = (
  room: RoomLike | undefined | null,
  tenant?: TenantReference | null,
): PlanRes | null => {
  const activePlan = roomActivePlan(room, tenant)
  if (activePlan) {
    return activePlan
  }
  const plannedPlan = roomPlannedPlan(room, tenant)
  if (plannedPlan) {
    return plannedPlan
  }
  return null
}

/**
 * 部屋の過去のテナントを取得（日付ベース）
 */
export const roomPastTenants = (
  room: RoomLike | undefined | null,
): TenantRes[] => {
  const tenants = (room as RoomRes | undefined | null)?.tenants as
    | TenantRes[]
    | undefined
  if (!tenants || tenants.length === 0) return []
  return tenantsLib.getPastTenants(tenants as any, null, null) as TenantRes[]
}

export const roomPastPlans = (room: RoomLike | undefined | null): PlanRes[] => {
  const plans = (room as RoomRes | undefined | null)?.plans as
    | PlanRes[]
    | undefined
  if (!plans || plans.length === 0) {
    return []
  }
  return plans.filter((plan) => planLib.isPast(plan, null))
}

export const roomSizeFormat = (room: RoomLike | undefined | null): string => {
  const roomRes = room as RoomRes | undefined | null
  if (!roomRes?.size) {
    return '未設定'
  }
  return `${roomRes.size}㎡`
}

export const roomFloorFormat = (room: RoomLike | undefined | null): string => {
  const roomRes = room as RoomRes | undefined | null
  if (roomRes?.floor === undefined || roomRes?.floor === null) {
    return '未設定'
  }
  return `${roomRes.floor}階`
}

/**
 * 部屋のサービスプラン情報を取得
 */
export const getRoomServicePlanInfo = (
  room: RoomLike | undefined | null,
): {
  label: string
  variant: 'plusPlan' | 'standardPlan' | 'echoesPlan' | 'nonePlan'
  /** Echoes 側でサービス提供が停止中(エコーズプランのみ true になり得る)。 */
  suspended: boolean
} => {
  const currentPlan = roomCurrentPlan(room)
  if (!currentPlan) {
    return { label: '未利用', variant: 'nonePlan', suspended: false }
  }
  const planMasterId = currentPlan.planMasterId
  const suspended = currentPlan.echoesPlanSuspended === true
  if (planMasterId === PLAN_MASTER_ID_PLUS) {
    return { label: 'プラスプラン', variant: 'plusPlan', suspended }
  }
  if (isEchoesPlanMasterId(planMasterId)) {
    return {
      label: getPlanMaster(planMasterId)?.name ?? 'エコーズプラン',
      variant: 'echoesPlan',
      suspended,
    }
  }
  return { label: 'スタンダードプラン', variant: 'standardPlan', suspended }
}

/**
 * 部屋のテナント情報を取得（契約期間付き）
 */
export const getRoomTenantInfo = (
  room: RoomLike | undefined | null,
): {
  name: string
  contractStartDate: string | null
  contractEndDate: string | null
} => {
  const tenant = roomActiveTenant(room)
  if (!tenant) {
    return {
      name: '未入居',
      contractStartDate: null,
      contractEndDate: null,
    }
  }
  return {
    name: tenant.name,
    contractStartDate: tenant.startDate,
    contractEndDate: tenant.endDate,
  }
}
